import React, { useEffect, useState } from 'react';
import { Button, Select } from 'antd';
import { DownOutlined, WalletOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import MobileAccountTabs from './components/MobileAccountTabs.jsx';
import { useNavigate } from 'react-router-dom';
import { GetDashboardApi, YourWallet } from '../../../api/request/dashboard.js';
import BalanceCard from './components/BalanceCardSM.jsx';
import { useSelector } from 'react-redux';
import PnLCardGridSM from './components/PnLCardGridSM.jsx';
import OpenTrades from './components/OpenTrades.jsx';
import RecentTableMobile from './components/RecentTableMobile.jsx';
import CreateLiveAccount from './components/CreateLiveAccount.jsx';
import Loader from '../../../components/Loader.jsx';
import { handleApiError } from '../../../utils/handleApiError.js';

// Sample data for the dashboard
const balanceCardsData = [
    { id: 1, type: 'Wallet Balance', amount: 0.00, color: 'bg-blue-500', gradient: 'from-[#EFF6FF] to-[#ffff]', icon: <WalletOutlined size={20} color='#fff' className='text-white' /> },
    { id: 2, type: 'Total Deposits', amount: 0.00, color: 'bg-green-500', gradient: 'from-[#ECFDF5] to-[#ffff]', icon: <PlusCircleOutlined size={20} color='#fff' className='text-white' /> },
    { id: 3, type: 'Total Withdraw', amount: 0.00, color: 'bg-teal-500', gradient: 'from-[#EDFFFF] to-[#ffff]', icon: <WalletOutlined size={20} color='#fff' className='text-white' /> }
];


const MobileDashboard = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [accountLogins, setAccountLogins] = useState([]);
    const [activeAccountLogins, setActiveAccountLogins] = useState('');
    const [data, setData] = useState([]);
    const [balanceCards, setBalanceCards] = useState(balanceCardsData);
    const { userDetails } = useSelector((state) => state.user);

    const filterAccounts = (data) => {
        const accountLogins = data?.filter((account) => account?.isLive)?.map((account) => account?.accountNo);
        setAccountLogins(accountLogins);
        setActiveAccountLogins(accountLogins?.[0]);
    };

    const getDashboard = async () => {
        try {
            const { data } = await GetDashboardApi();
            if (data?.success) {
                setData(data?.data);
                setBalanceCards(prev => prev.map(item => {
                    if (item.id === 2) return { ...item, amount: data?.data?.depositeAmount };
                    if (item.id === 3) return { ...item, amount: data?.data?.withdrawAmount };
                    return item;
                }));
                filterAccounts(data?.data?.mT5AccountList);
                setIsLoading(false);
            }
        } catch (error) { }
    }

    const getWallet = async () => {
        try {
            const { data } = await YourWallet(userDetails?._id);
            if (data?.success) {
                setBalanceCards(prev => prev.map(item => item.id === 1 ? { ...item, amount: data?.data?.walletAmount } : item));
            }
        } catch (error) { }
    };

    const fetchAllData = async () => {
        try {
            setIsLoading(true);
            await Promise.all([getDashboard(), getWallet()]);
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchAllData();
    }, []);

    if (isLoading) return <Loader />;

    if (!accountLogins?.length) return <CreateLiveAccount />;

    return (
        <div className="mobile-dashboard min-h-screen">
            <div className="mb-4">
                <div className="relative">
                    {accountLogins.length > 0 && <Select
                        value={activeAccountLogins}
                        onChange={setActiveAccountLogins}
                        className="w-full !bg-white rounded-xl"
                        size="large"
                        suffixIcon={<div className="flex items-center justify-center h-full w-full"><DownOutlined className="text-gray-400 bg-[#F5F9FD] p-2.5 rounded-lg" /></div>}
                        popupClassName="custom-select-dropdown"
                        dropdownAlign={{ offset: [0, 0] }}
                        dropdownStyle={{
                            borderRadius: '16px',
                            padding: '8px',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                            marginTop: '-10px'
                        }}
                        optionLabelProp="label"
                    >
                        {accountLogins.map(id => (
                            <Select.Option
                                key={id}
                                value={id}
                                label={<span className="primary font-bold text-base">{id}</span>}
                            >
                                <div className="flex items-center justify-between py-1 px-2 rounded-lg">
                                    <span className={`font-medium text-base ${activeAccountLogins === id ? 'primary' : 'text-gray-700'
                                        }`}>
                                        {id}
                                    </span>
                                    {activeAccountLogins === id && (
                                        <IoCheckmarkDoneSharp className="primary text-sm ml-4" size={22} />
                                    )}
                                </div>
                            </Select.Option>
                        ))}
                    </Select>}
                </div>
            </div>

            <div className="space-y-6">
                {/* Balance Cards */}
                <div>
                    <div className="grid grid-cols-3 gap-3">
                        {balanceCards.map((card, index) => (
                            <BalanceCard
                                key={card.id || index}
                                type={card.type}
                                amount={card.amount}
                                color={card.color}
                                gradient={card.gradient}
                                icon={card.icon}
                                prefix={"$"}
                            />
                        ))}
                    </div>
                </div>

                <PnLCardGridSM loginId={activeAccountLogins} />

                {/* Open Trades */}
                <OpenTrades loginId={activeAccountLogins} />

                {/* Trading Accounts */}
                <MobileAccountTabs data={data?.mT5AccountList} />

                {/* Recent Transactions */}
                <RecentTableMobile data={data} />

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                    <Button type="primary" className="h-12 bg-primary text-white" onClick={() => navigate("/account?type=live", { state: { title: 'Open Live Account' } })}>
                        Open Live Account
                    </Button>
                    <Button type="primary" className="h-12 bg-primary text-white" onClick={() => navigate("/account?type=demo", { state: { title: 'Open Demo Account' } })}>
                        Open Demo Account
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MobileDashboard;